<!--赖传峰-->
<!--2022/4/6-->
<script
  lang="ts"
  setup
>
import {
  computed,
  defineAsyncComponent,
  inject,
  navigateTo,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
  useI18n,
  useNuxtApp,
  useRoute,
  useRouter,
  watch,
} from '#imports'
import {
  getBasicConfig,
  type GetShareDetailQuery,
  getShareFileInfo,
  type GetShareQuery,
} from '@/api'
import { checkAllowOpenFileExtensions } from '@/components/share/consumer/consumer.script'
import FileExtensionPage from '@/components/share/consumer/index.vue'
import { ContextMenuEventName } from '@/components/share/contextMenu.script'
import {
  type ContextMenuExpose,
  ContextMenuPositionPreference,
  Operation,
} from '@/components/share/contextMenu.type'
import ContextMenu from '@/components/share/ContextMenu.vue'
import {
  createTorrentTask,
  hasAttrInEventPath,
  hasMaskElm,
  isVideoOrImage,
  recordSelected,
  RESTORE_TELEPORT_ID,
  RESTORE_TELEPORT_MOBILE_ID,
  useWebDecompression,
} from '@/components/share/fileExplorer.script'
import FileExplorerHeader from '@/components/share/FileExplorerHeader.vue'
import FileItem from '@/components/share/FileItem.vue'
import FileItemWaterfall from '@/components/share/FileItemWaterfall.vue'
import RestoreFilesButton from '@/components/share/RestoreFilesButton.vue'
import {
  handleAutoSort,
  isFolder,
  shareTrackParams,
} from '@/components/share/share.script'
import ShareAuthor from '@/components/share/ShareAuthor.vue'
import SwipeSelectBox, { type SwipeSelectBoxState } from '@/components/share/SwipeSelectBox.vue'
import useFileManager, {
  closeFileManager,
  hasFileManagerVisible,
} from '@/composables/useFileManager'
import useFullScreenLoading from '@/composables/useFullScreenLoading'
import useMessage from '@/composables/useMessage'
import useParentId from '@/composables/useParentId'
import useShareId from '@/composables/useShareId'
import { useLayout } from '@/store/layout'
import { useShare } from '@/store/share'
import $track from '@/track/webShare'
import {
  CustomEventType,
  DriveKind,
  InjectionKey,
  SettingKey,
  StorageKey,
  ViewType,
} from '@/types/enum'
import {
  authByLocalStorage,
  isAudio,
  isImage,
  isMacOS,
  isTorrentFile,
  isVideo,
  isZippedFile,
} from '@/utils'
import {
  getElementOffsetTop,
  setProperty2Element,
} from '@/utils/dom'
import initBindSelectEventDirective from '@/utils/initBindSelectEventDirective'
import once from '@/utils/once'
import {
  useElementBounding,
  useEventBus,
  useEventListener,
  useMounted,
  useThrottleFn,
} from '@vueuse/core'
import btDecode from '@xunlei/bencode-worker'
import { ElScrollbar } from 'element-plus'
import _ from 'lodash'
import {
  type Component,
  hydrateOnVisible,
} from 'vue'
import type { LocationQueryValue } from 'vue-router'
import {
  getUserSetting,
  setUserSetting,
} from '~/api/linkHub'
import useDisplayMode from '~/composables/useDisplayMode'
import useShareFiles from '~/composables/useShareFiles'

const props = defineProps<{
  showFileInfo: boolean
}>()
// 最近一次选中文件，是否使用的全选操作。如果是全选，则滚动加载新数据后，也需要被选中
let isSelectAll = false
// 上一次被选中区域的开始与结束的下标
let latestSelectedIndex: [start?: number, end?: number] = []
// 一次滑动选中的时候，曾经被包含过的文件
let beenSwipedIds: string[] = []
// 当前被滑动选中区域的开始与结束的下标
let swipedIndex: [start?: number, end?: number] = []

const AsyncFileExplorerAddon = defineAsyncComponent(() => import('@/components/share/FileExplorerAddon.vue'))

const GridTypeComponentMap: { [key in ViewType]?: Component } = {
  [ViewType.GRID]: FileItem,
  [ViewType.LIST]: FileItem,
  [ViewType.FLOW]: FileItemWaterfall,
}

const {
  t,
  locale,
} = useI18n()
const shareDetail = inject<ShareDetailRef>(InjectionKey.SHARE_DETAIL)
const showLoginDialog = inject<ShowLoginDialog>(InjectionKey.SHOW_LOGIN_DIALOG)

const contextMenuBus = useEventBus<Operation, string[]>(ContextMenuEventName)
const listContainer = ref<HTMLElement>()
const listContent = ref<HTMLElement>()
const contextMenuRef = ref<ContextMenuExpose>()
const scrollBar = ref<InstanceType<typeof ElScrollbar>>()
const restoreBtn = ref<InstanceType<typeof RestoreFilesButton>>()
const restoreTeleportId = ref<string>()
const playerRestoreRef = ref<typeof RestoreFilesButton>()
const recordHeaderHeight = ref(100)
const showBlockBottomButton = ref(false)
const isMounted = useMounted()
// 在滑动选中的时候，禁用列表滚动
const isSwiping = ref(false)
const { viewType } = useDisplayMode()
const infiniteScrollLoading = ref(false)
const fileExtensionPageRef = ref<typeof FileExtensionPage>()

const shareId = useShareId()
const parentId = useParentId()
const shareStore = useShare()
const layoutStore = useLayout()
const route = useRoute()
const router = useRouter()
const {
  left: listContentLeft,
  right: listContentRight,
} = useElementBounding(listContent)
const { $getPasscodeToken } = useNuxtApp()
const passcodeToken = $getPasscodeToken()
const { isMobile } = toRefs(layoutStore)

// 是否将某些头部元素放入可滚动容器中

const files = useShareFiles() // NOTICE 不可放入 computed 中执行
const list = computed(() => files.value.map(el => ({
  ...el,
  thumbnail_link: props.showFileInfo ? el.thumbnail_link : '',
  name: props.showFileInfo ? el.name : '******',
})) || [])
const isSelectedAll = computed(() => shareStore.selectedIds.length > 0
  && shareStore.selectedIds.length === list.value.length)
const isAllowSwipe = computed(() => [ViewType.GRID, ViewType.LIST].includes(viewType.value))
const pageToken = computed(() => parentId.value && shareDetail?.value?.next_page_token)
const includesHeaderMode = computed(() => isMobile.value && list.value.length > 0)
// 如果是电脑，只有单个图片与视频使用单图模式；如果是手机，则不限制文件类型
const isSingleFileMode = computed(() => !parentId.value
  && list.value.length === 1
  && list.value[0].kind === DriveKind.FILE
  && (isMobile.value ? true : isVideoOrImage(list.value[0].file_extension, list.value[0].mime_type)))

const AsyncScrollLoadingAnimation = defineAsyncComponent({
  loader: () => import('@/components/LottieAnimation.vue'),
  hydrate: hydrateOnVisible(),
})
const lottieData = async () => (await import('./loadingLottieData.json')).default

const restore = () => {
  $track.web_share_page_click({
    ...shareTrackParams(),
    button: 'more_save',
  })
  restoreBtn.value?.click()
}

const findFileIndexById = (id: string): number => list.value.findIndex(el => el.id === id)

const recordSelectIds = (needCompare = false): void => {
  const selectIds = recordSelected.range.reduce((ids, i) => {
    const id = list.value[i]?.id
    id && ids.push(id)
    return ids
  }, [] as string[])
  if (!needCompare) {
    shareStore.setSelectedIds(selectIds)
    return
  }
  if (selectIds.sort().join() === shareStore.selectedIds.sort().join()) return
  shareStore.setSelectedIds(selectIds)
}

/* 处理文件选中交互的指令 */
const vBindSelectEvent = initBindSelectEventDirective({ recordSelectIds, findFileIndexById })

// region 是否直接查看从搜索页自动打开的分享内容
const showCoverAboveViewport = ref(false) // 如果是从搜索页自动打开，并且用户没有设置直接显示搜索内容，那么就在分享上面加一层毛玻璃遮罩
const showCoverAbovePlayer = computed(() => useShare().data.params?.auto_play === String(true)) // 如果有全屏播放视频，需要在播放器上显示毛玻璃遮罩
const shareCode = computed(() => (import.meta.client ? window.parent.name.split('_')
  .reverse()[0] : ''))
const hideTemporary = (button: Parameters<typeof $track.share_code_jump_confirmation_click>[0]['button'] = 'view') => {
  $track.share_code_jump_confirmation_click({
    ...shareTrackParams(),
    button,
    code_text: shareCode.value,
  })
  showCoverAboveViewport.value = false
  sessionStorage.setItem(StorageKey.HIDDEN_SHARED_CONTENT_COVER, String(true))
}

const setShowSharedContentFromSearch = async () => {
  if (!showCoverAboveViewport.value) return
  const {
    data,
    error,
  } = (await setUserSetting(SettingKey.SHOW_SHARED_CONTENT_FROM_SEARCH, String(true)))
  if (data.value) return hideTemporary('view_without_prompting')
  if (error.value?.message) {
    useMessage({
      type: 'error',
      message: error.value.message,
    })
  }
}

onMounted(async () => {
  await router.isReady()
  const parentName = window.parent.name || (route.query.parent_name as LocationQueryValue)// 桌面客户端只能使用 query 携带 parent name
  showCoverAboveViewport.value = !!parentName?.startsWith('pp_auto_open')
    && sessionStorage.getItem(StorageKey.HIDDEN_SHARED_CONTENT_COVER) !== String(true)
    && (await getBasicConfig(locale.value)).data.value?.values.basic.share_code_tips !== false
    && (await getUserSetting([SettingKey.SHOW_SHARED_CONTENT_FROM_SEARCH]))
      .data.value?.settings[SettingKey.SHOW_SHARED_CONTENT_FROM_SEARCH] === String(false)
})

watch(
  showCoverAboveViewport,
  val => {
    val && $track.share_code_jump_confirmation_show({
      ...shareTrackParams(),
      code_text: shareCode.value,
    })
    window.dispatchEvent(new CustomEvent(CustomEventType.SET_VIDEO_FREEZE, { detail: { val } }))
  },
)
// endregion

const loadShareData = async () => {
  const method = parentId.value
    ? shareStore.setShareDetail
    : shareStore.setShare
  const query = {
    share_id: shareId.value,
    parent_id: parentId.value,
    pass_code_token: passcodeToken.value,
  } as (GetShareQuery & GetShareDetailQuery)
  infiniteScrollLoading.value = true
  await method(query)
  infiniteScrollLoading.value = false
}

const infiniteScroll = async () => {
  if (!pageToken.value || !parentId.value) return

  const query = {
    share_id: shareId.value,
    parent_id: parentId.value,
    pass_code_token: passcodeToken.value,
    limit: 50,
    page_token: pageToken.value,
  }

  infiniteScrollLoading.value = true
  await shareStore.setShareDetail(query)
  infiniteScrollLoading.value = false
}

const emitLogin = () => new Promise((resolve, reject) => {
  if (authByLocalStorage()) {
    resolve(true)
    return
  }
  // 未登录，需要登录
  showLoginDialog?.({
    afterLogin() {
      resolve(true)
    },
    afterClose() {
      if (!authByLocalStorage()) {
        // eslint-disable-next-line
        reject(false)
      }
    },
  })
})

const { show: showPaymentPane } = useWebDecompression()

const canNotOpenFile = () => useMessage({
  type: 'warning',
  message: t('can-not-open-the-file'),
})
const openFile = async (file?: PPFile) => {
  if (!file) {
    canNotOpenFile()
    return
  }

  const {
    id,
    kind,
    file_extension: fileExtension,
    mime_type: mimeType,
    size,
    // thumbnail_link: thumbnailLink,
  } = file
  $track.web_share_page_click({
    ...shareTrackParams(),
    button: isFolder(kind) ? 'open_folder' : 'open',
  })
  if (isMobile.value) {
    // 移动端单击不显示选区
    setTimeout(() => {
      shareStore.setSelectedIds([])
    }, 300)
  }

  const getFileInfo = () => getShareFileInfo({
    share_id: shareId.value,
    file_id: id,
    pass_code_token: useNuxtApp()?.$getPasscodeToken()?.value,
  })

  if (isFolder(kind)) return navigateTo({ path: `${route.path}/${id}` })

  if (isVideo(fileExtension, mimeType) || isImage(fileExtension, mimeType) || isAudio(fileExtension, mimeType)) {
    const loadingBeforePreview = useFullScreenLoading()
    document.querySelector('.circular')
      ?.setAttribute('viewBox', '0 0 24 24')
    const data = (await getFileInfo()).data.value

    // 分享失效判断
    if (data.share_status !== 'OK') {
      useMessage({
        type: 'error',
        message: data?.share_status_text || 'Preview Fail',
      })
      loadingBeforePreview.close()
      return
    }

    sessionStorage.setItem(StorageKey.PREVIEW_FILE_ID, id)
    // push一条记录，避免系统后退时直接返回上一级目录
    window.history.pushState('media-preview', '', window.location.href)

    return useFileManager(list.value, id, 'share_page', {
      share_id: shareId.value,
      pass_code_token: useNuxtApp()?.$getPasscodeToken()?.value,
      t,
    }, (error) => {
      loadingBeforePreview.close()

      if (!error) {
        setTimeout(() => {
          restoreTeleportId.value = isMobile.value
            ? `#${RESTORE_TELEPORT_MOBILE_ID}`
            : `#${RESTORE_TELEPORT_ID}`
        }, 1000)
      }
    })
  }

  if (isZippedFile(fileExtension, mimeType)) {
    const data = (await getFileInfo()).data.value
    const accessLink = data.file_info.apps?.[0]?.link || ''
    const webDecompressLink = accessLink.replace(/\/drive\/decompression/, '/drive/web-decompression')
    // const webDecompressLink = accessLink.replace(/dev.mypikpak.com\/drive\/decompression/, 'www.mypikpak.com/drive/web-decompression')
    webDecompressLink && showPaymentPane(webDecompressLink)
    return
  }

  /* 小于 10MB 的种子文件，解析出磁力链并创建任务 */
  if (Number(size) <= 10 * 1024 * 1024 && isTorrentFile(fileExtension, mimeType)) {
    let msgResult: ReturnType<typeof useMessage> | undefined
    try {
      await emitLogin()
      msgResult = useMessage({
        message: t('decoding-torrent'),
        type: 'null',
        duration: 60 * 1000,
      })
      const data = (await getFileInfo()).data.value
      const webContentLink = data.file_info.web_content_link
      const res = await fetch(webContentLink)
      const magnet = await btDecode(await res.arrayBuffer())
      createTorrentTask(magnet)
    } catch (err) {
      if (err) {
        console.error('Decode torrent file failed', err)
        useMessage({
          type: 'error',
          message: t('single-torrent-file-error'),
        })
      }
    } finally {
      msgResult?.close?.()
    }
    return
  }

  // 以独立页面消费的文件类型
  if (checkAllowOpenFileExtensions(fileExtension)) {
    const passCodeToken = useNuxtApp()?.$getPasscodeToken()?.value
    const data = (await getFileInfo()).data.value
    const fileInfo = data.file_info
    return fileExtensionPageRef.value?.setFile({
      id,
      name: fileInfo.name,
      folderType: fileInfo.folder_type,
      url: fileInfo?.apps?.[0]?.link,
      fileExtension,
      hash: fileInfo.hash,
    }, shareId.value, passCodeToken)
  }
  canNotOpenFile()
}

/* 双击事件 */
const handleFileDblclick = (e: MouseEvent, file: PPFile) => {
  if (!hasAttrInEventPath(e, 'data-file-event')) return
  openFile(file)
}

const resetRestoreTeleportId = () => restoreTeleportId.value = undefined

// region 事件响应
/**
 * 在行上鼠标右键
 * 如果当前只有一行被选中，或者被选中行不是打开菜单的行，则切换选中，打开右键菜单
 * 如果当前有多行被选中，并且被选中行是打开菜单的行，不切换选中结果，打开右键菜单
 */
const handleRowContextMenu = (e: MouseEvent, { id }: PPFile) => {
  !shareStore.selectedIds.includes(id) && (shareStore.setSelectedIds([id]))

  contextMenuRef.value?.show(e, shareStore.selectedIds)
}
/**
 * 点击选中行，最右边的操作 Button，上下文菜单当成下拉菜单使用
 * 需要手动设置定位的坐标
 * TODO: 统一定位到菜单右上角，调整垂直间距
 */
const handleRowOperationClick = (target: Element, { id }: PPFile) => {
  !shareStore.selectedIds.includes(id) && (shareStore.setSelectedIds([id]))

  const {
    bottom,
    right,
  } = target.getBoundingClientRect()
  contextMenuRef.value?.show(
    {
      x: right,
      y: bottom,
      preference: ContextMenuPositionPreference.BOTTOM_RIGHT,
    },
    shareStore.selectedIds,
  )
}
/* 移动端单击 */
const openFileInMobile = (e: MouseEvent, file: PPFile) => {
  if (hasAttrInEventPath(e, 'data-active-by-click')) {
    return openFile(file)
  }

  if (!hasAttrInEventPath(e, 'data-file-event') || !isMobile.value || shareStore.mobileSelectMode) return
  openFile(file)
}

/* 右键菜单的处理程序 */
const handleContextMenuItemClick = async (operation: Operation, ids?: string[]) => {
  switch (operation) {
    case Operation.OPEN:
      // eslint-disable-next-line no-case-declarations
      const ids_ = ids || []
      return openFile(list.value.find(({ id }) => id === ids_[0])!)
    case Operation.RESTORE:
      return restore()
    default:
      console.error('Invalid contextmenu item')
  }
}
// region 移动端的事件
const handleSelectAll = () => {
  isSelectAll = !isSelectAll
  shareStore.setSelectedIds(isSelectAll ? list.value.map(el => el.id) : [])
}
// endregion

let lastScrollTop = 0
const updateCollapseStatus = useThrottleFn((scrollTop: number) => {
  if (!isMobile.value) return
  const scrollEl: HTMLElement | undefined = scrollBar.value?.wrapRef
  const footerHeight = 74
  const isMovingDown = scrollTop > lastScrollTop
  const isMovingUp = scrollTop < lastScrollTop

  if (!scrollEl) return
  // 响应这个区间内的滚动
  const range = [recordHeaderHeight.value - 72, scrollEl.scrollHeight - scrollEl.offsetHeight - footerHeight]

  if (range[0] <= scrollTop && scrollTop < range[1]) {
    // NOTE: 注意 scrollTop === lastScrollTop 时的状态是不处理
    if (isMovingDown) {
      layoutStore.isCollapse = true
    } else if (isMovingUp) {
      layoutStore.isCollapse = false
    }
  } else if (scrollTop < range[0]) {
    layoutStore.isCollapse = false
  }

  lastScrollTop = scrollTop
}, 50, undefined, true)

const handleScroll = (e: { scrollLeft: number; scrollTop: number }) => {
  contextMenuRef.value?.close?.()

  if (isMobile.value) {
    if (!showBlockBottomButton.value && e.scrollTop > 0) {
      showBlockBottomButton.value = true
    }
    updateCollapseStatus(e.scrollTop)
  }
}

// region 滑动选择框的事件
const handleSwipeStart = () => {
  isSwiping.value = true
  swipedIndex = []
  beenSwipedIds = []
}

const handleSwiping = (e: MouseEvent, {
  left,
  top,
  width,
  height,
}: Omit<SwipeSelectBoxState, 'visible'>) => {
  if (viewType.value === ViewType.SINGLE) return

  const bottom = top + height
  const right = left + width

  /* 水平方向不相交 */
  if (right < listContentLeft.value || left > listContentRight.value) return shareStore.setSelectedIds([])

  /* 不同视图模式下，选中框与元素的相交判断 */
  const intersectAsset: { [key in (ViewType.LIST | ViewType.GRID)]: (el: Element) => boolean } = {
    [ViewType.LIST]: (el: Element) => {
      const {
        top: elTop,
        bottom: elBottom,
      } = el.getBoundingClientRect()
      return [
        elTop > top && elTop < bottom, // 文件的上边与选择框相交
        elTop > top && elBottom < bottom, // 文件被选择框包含
        elBottom > top && elBottom < bottom, // 文件的下边与选择框相交
        elTop < top && elBottom > bottom, // 文件包含选择框
      ].some(asset => asset)
    },
    [ViewType.GRID]: (el: Element) => {
      const target = el.querySelector('[data-swiping]')// 需要响应选中框的元素

      if (!target) return false

      const {
        top: elTop,
        right: elRight,
        bottom: elBottom,
        left: elLeft,
      } = target.getBoundingClientRect()

      /* NOTICE 以下注释是选择框的视角，顺时针 */
      return [
        elTop < top && top < elBottom && left < elLeft && right > elRight, // 上边与元素相交
        elTop < top && top < elBottom && elLeft < right && right < elRight, // 右上角与元素相交
        elLeft < right && right < elRight && top < elTop && bottom > elBottom, // 右边与元素相交
        elLeft < right && right < elRight && elTop < bottom && bottom < elBottom, // 右下角与元素相交
        elTop < bottom && bottom < elBottom && left < elLeft && right > elRight, // 下边与元素相交
        elTop < bottom && bottom < elBottom && elLeft < left && left < elRight, // 左下角与元素相交
        elLeft < left && left < elRight && top < elTop && bottom > elBottom, // 左边与元素相交
        elLeft < left && left < elRight && elTop < top && top < elBottom, // 左上角与元素相交
        top < elTop && right > elRight && bottom > elBottom && left < elLeft, // 包含元素
        top > elTop && right < elRight && bottom < elBottom && left > elLeft, // 被元素包含
      ].some(asset => asset)
    },
  }

  const swipeSelectedIds = Array
    .from(listContent.value!.children)
    .filter(intersectAsset[viewType.value as (ViewType.LIST | ViewType.GRID)])
    .map(({ id }) => id) // NOTICE 这里的 id，是元素上的 id 属性

  beenSwipedIds = [...new Set([...beenSwipedIds, ...swipeSelectedIds])]
  swipedIndex = [findFileIndexById(swipeSelectedIds[0]), findFileIndexById(swipeSelectedIds.slice(-1)[0])]
  const excludeIds = beenSwipedIds.filter((id) => !swipeSelectedIds.includes(id))

  const selectedIds = (() => {
    if (isMacOS() ? e.metaKey : e.ctrlKey) {
      return swipeSelectedIds
        .concat(shareStore.selectedIds)
        .filter((id) => !excludeIds.includes(id))
    }

    if (e.shiftKey && latestSelectedIndex.length) {
      const startIndex = Math.min(swipedIndex[0] as number, ...latestSelectedIndex as number[])
      const endIndex = Math.max(swipedIndex[1] as number, ...latestSelectedIndex as number[])

      return list.value
        .slice(startIndex, endIndex + 1)
        .map(({ id }) => id)
        .concat(shareStore.selectedIds)
        .filter(id => !excludeIds.includes(id))
    }

    return swipeSelectedIds
  })()
    .filter((el, idx, arr) => arr.indexOf(el) === idx)

  // 滑动过程中会不断触发事件，所以判断前后的 id 是否有变化，没有就不再设置
  if ([...new Set(selectedIds)].sort()
    .join() === shareStore.selectedIds.sort()
    .join()) {
    return
  }

  shareStore.setSelectedIds(selectedIds)
}

const handleSwipeDone = () => {
  isSwiping.value = false
  swipedIndex.length && (latestSelectedIndex = swipedIndex) // 如果没有选中文件，不设置 latestSelectedIndex
  isSelectAll = shareStore.selectedIds.length === list.value.length
}
// endregion
// endregion 事件响应

/* 如果当前是全选，滚动加载的数据也需要被全选 */
const shareAction = shareStore.$onAction(({
  name,
  args,
  after,
}) => {
  if (name === 'setShareDetail' && isSelectAll && args[0].page_token) {
    after(() => shareStore.setSelectedIds(_.map(list.value, 'id')))
  }
})

const handlePlayerRestore = ({ detail }: CustomEvent) => {
  playerRestoreRef.value?.click && playerRestoreRef.value.click(detail?.scene)
}

const updateHeaderHeight = () => {
  if (isMobile.value) {
    // 活动横幅的渲染有些延迟
    setTimeout(() => {
      const allHeaderHeight = getElementOffsetTop(listContent.value || null, true)
      recordHeaderHeight.value = allHeaderHeight
      setProperty2Element(undefined, '--all-header-height', `${allHeaderHeight}px`)
    }, 1000)
  }
}

watch(locale, updateHeaderHeight)

onMounted(() => {
  window.addEventListener(CustomEventType.FILE_MANAGER_CLOSE, resetRestoreTeleportId)
  window.addEventListener(CustomEventType.PLAYER_RESTORE, handlePlayerRestore)
  updateHeaderHeight()
  contextMenuBus.on(handleContextMenuItemClick)
})

onUnmounted(shareAction)
onUnmounted(() => {
  window.removeEventListener(CustomEventType.FILE_MANAGER_CLOSE, resetRestoreTeleportId)
  window.removeEventListener(CustomEventType.PLAYER_RESTORE, handlePlayerRestore)
  contextMenuBus.off(handleContextMenuItemClick)
})

watch(() => route.params, (val, oldValue) => {
  if (val.share_id === oldValue.share_id && _.isEqual(val.parent_ids, oldValue.parent_ids)) return
  shareStore.mobileSelectMode = false
  showBlockBottomButton.value = false
  scrollBar.value?.setScrollTop(0)
  shareStore.setSelectedIds([])
})
watch(() => list.value.length, val => {
  if (!val) {
    shareStore.mobileSelectMode = false
  }
})
watch(() => shareStore.mobileSelectMode, val => !val && shareStore.setSelectedIds([]))

/* 使用全选快捷键 */
useEventListener('keydown', (e: KeyboardEvent) => {
  /* INPUT 与 TEXTAREA 全选的时候，文件不能被选中 */
  if (['INPUT', 'TEXTAREA'].includes((e.target as Element).tagName)) return

  if ((isMacOS() ? e.metaKey : e.ctrlKey) && e.key.toLocaleLowerCase() === 'a') {
    shareStore.setSelectedIds(_.map(list.value, 'id'))
    isSelectAll = true
  }
})
/**
 * 取消当前文件选中的逻辑
 * 为什么不监听 click 呢？ 因为 click 包含 mousedown 与 mouseup，导致选择框在元素之外的区域松开时，会取消选中元素
 */
useEventListener('mousedown', (e: MouseEvent) => {
  if (!shareStore.selectedIds.length) return // 如果当前是滑动选中状态，不取消选中；没有被选中的文件，也不需要操作

  const eventElePath = hasAttrInEventPath(e)
  const whiteListDOM = [
    restoreBtn.value?.dom(),
    document.getElementById('multi_operation'),
    document.getElementById('report-shared'),
    contextMenuRef.value?.dom(),
  ] as HTMLElement[]

  // 由白名单元素内部触发事件，不取消选中
  if (eventElePath.some(el => whiteListDOM.includes(el) || el.attributes?.getNamedItem('data-file-event'))) return

  if (hasMaskElm(eventElePath)) return

  shareStore.setSelectedIds([])
  latestSelectedIndex = []
  isSelectAll = false
  recordSelected.reset()
})

const openFileOnce = once((file: PPFile) => openFile(file))

watch(() => route.params, () => {
  const videoFile = handleAutoSort(route)
  videoFile && openFileOnce(videoFile)
})

watch(() => route.path, () => hasFileManagerVisible() && closeFileManager())

/* 通过 URL 打开文件 */
watch(() => props.showFileInfo, val => {
  if (import.meta.server || !val) return
  const videoFile = handleAutoSort(route)
  videoFile && openFileOnce(videoFile)
}, { immediate: true })
// end region
</script>

<template>
  <div
    :class="{'single-file': isSingleFileMode}"
    class="file-explorer"
  >
    <div
      v-if="!includesHeaderMode"
      style="display: flex; align-items: center; justify-content: space-between; flex: 0;"
    >
      <ShareAuthor :show-cover-above-viewport="showCoverAboveViewport" />
      <div v-if="!isMobile && !showCoverAboveViewport">
        <RestoreFilesButton ref="restoreBtn" />
      </div>
    </div>

    <div class="files-viewport">
      <div
        v-if="isSingleFileMode"
        class="single-file"
      >
        <ElDivider
          v-if="!isMobile"
          style="border-color: #f0f0f0; margin: 16px 0;"
        />
        <FileItem
          :file="list[0]"
          :mobile-select-mode="false"
          :view-type="ViewType.SINGLE"
          @click="openFile(list[0])"
          @dblclick="openFile(list[0])"
          @contextmenu.stop.prevent="(e: MouseEvent) => !isMobile && handleRowContextMenu(e, list[0])"
        />
        <div v-if="isMobile" class="files-bottom fixed-type">
          <RestoreFilesButton ref="restoreBtn" />
          <div
            v-if="showCoverAboveViewport"
            class="cover-above-viewport"
          />
        </div>
      </div>
      <template v-else>
        <FileExplorerHeader
          v-if="!includesHeaderMode"
          :handle-open-file="() => openFile(list.find(el=>shareStore.selectedIds.includes(el.id)))"
          :handle-restore="restore"
          :handle-select-all="handleSelectAll"
          :load-share-data="loadShareData"
          :mobile-select-mode="shareStore.mobileSelectMode"
          :is-selected-all="isSelectedAll"
        />
        <div
          v-if="list.length"
          ref="listContainer"
          class="list-container"
        >
          <ElScrollbar
            ref="scrollBar"
            :wrap-style="{overflow: isSwiping ? 'hidden': 'auto'}"
            max-height="100%"
            @scroll="handleScroll"
          >
            <!-- 这个元素是为了同时能让滚动条贴右边显示 -->
            <div
              class="list-content"
              :data-header-offset-layout="isMobile"
            >
              <div class="mobile-header-wrap-1" />
              <div
                v-if="isMobile"
                style="display: flex; align-items: center; justify-content: space-between; flex: 0;"
              >
                <ShareAuthor :show-cover-above-viewport="showCoverAboveViewport" />
              </div>
              <div
                class="mobile-header-wrap-3"
                style="display: inline;"
              >
                <!-- display: inline; 让里面的子元素粘性定位生效 -->
                <FileExplorerHeader
                  v-if="isMobile"
                  :handle-open-file="() => openFile(list.find(el=>shareStore.selectedIds.includes(el.id)))"
                  :handle-restore="restore"
                  :handle-select-all="handleSelectAll"
                  :load-share-data="loadShareData"
                  :mobile-select-mode="shareStore.mobileSelectMode"
                  :is-selected-all="isSelectedAll"
                />
              </div>
              <ol
                ref="listContent"
                v-infinite-scroll="infiniteScroll"
                :class="viewType"
                :infinite-scroll-immediate="false"
                class="file-list"
              >
                <li
                  v-for="(file, index) in list"
                  :id="file.id"
                  :key="file.id"
                  v-bind-select-event="file"
                  :aria-label="file.name"
                  :class="{
                    checked: shareStore.selectedIds.includes(file.id),
                    [viewType]: true,
                    'current-path': recordSelected.lastPath === index,
                  }"
                  class="row"
                  data-file-event
                  @click="(e: MouseEvent) => openFileInMobile(e, file)"
                  @dblclick="e => !shareStore.mobileSelectMode && handleFileDblclick(e, file)"
                  @contextmenu.stop.prevent="e => !isMobile && handleRowContextMenu(e, file)"
                >
                  <component
                    :is="GridTypeComponentMap[viewType]"
                    :class="{
                      checked: shareStore.selectedIds.includes(file.id),
                      'current-path': recordSelected.lastPath === index,
                    }"
                    :file="file"
                    :mobile-select-mode="shareStore.mobileSelectMode"
                    :view-type="viewType"
                    @open-file="openFile"
                    @click-show-context-menu="handleRowOperationClick"
                  />
                </li>
              </ol>
              <template v-if="isMobile">
                <!-- 非浮动按钮 -->
                <div
                  class="files-bottom"
                  :class="[(!showBlockBottomButton || !layoutStore.isCollapse) && 'invisible']"
                >
                  <RestoreFilesButton />
                  <div
                    v-if="showCoverAboveViewport"
                    class="cover-above-viewport"
                  />
                </div>
                <!-- 浮动按钮 -->
                <div
                  class="files-bottom fixed-type"
                  :class="[
                    layoutStore.isCollapse && 'is-collapse',
                  ]"
                >
                  <RestoreFilesButton ref="restoreBtn" />
                  <div
                    v-if="showCoverAboveViewport"
                    class="cover-above-viewport"
                  />
                </div>
              </template>
            </div>
            <div
              v-if="pageToken"
              :style="{height: '80px', marginBottom: viewType === ViewType.GRID ? '18px': undefined}"
            >
              <AsyncScrollLoadingAnimation
                v-if="infiniteScrollLoading"
                :animation-data="lottieData"
                autoplay
                style="height: 100%;"
              />
            </div>
          </ElScrollbar>
        </div>
        <!--目录为空-->
        <div
          v-else
          class="empty-folder"
        >
          <span>{{ $t('empty-folder') }}</span>
        </div>
      </template>
      <div
        v-if="showCoverAboveViewport"
        :class="showCoverAbovePlayer ? 'fullscreen' : undefined"
        class="cover-above-viewport"
      >
        <div class="content">
          <!-- 这是一个占位元素，为了在移动端让剩下两个元素一个居中，一个在底部 justify-content: space-between;  -->
          <div
            v-if="isMobile"
            class="shadow-ele"
          />
          <div>
            <div class="icon" />
            <div class="desc">
              {{ $t('shared-content-cover-desc') }}
            </div>
            <PPButton
              style="min-width: 120px; margin-top: 16px; height: 36px;"
              type="primary"
              @click="() => hideTemporary()"
            >
              {{ $t('view-now') }}
            </PPButton>
          </div>
          <div class="operation">
            <i18n-t keypath="shared-content-cover-operation">
              <template #viewSharedContentWithoutConfirm>
                <a
                  href="javascript:void(0)"
                  @click="setShowSharedContentFromSearch"
                >
                  {{ $t('view-shared-content-without-confirm') }}
                </a>
              </template>
            </i18n-t>
          </div>
        </div>
      </div>
    </div>
    <ClientOnly>
      <Teleport
        v-if="restoreTeleportId"
        :to="restoreTeleportId"
      >
        <RestoreFilesButton
          ref="playerRestoreRef"
          preview
        />
      </Teleport>
    </ClientOnly>
  </div>
  <SwipeSelectBox
    v-if="listContainer && listContent && !isMobile && isAllowSwipe"
    :trigger-element="listContainer!"
    @done="handleSwipeDone"
    @start="handleSwipeStart"
    @swipe="handleSwiping"
  />
  <ContextMenu
    ref="contextMenuRef"
    @item-click="handleContextMenuItemClick"
  />
  <ClientOnly>
    <FileExtensionPage
      ref="fileExtensionPageRef"
    />
    <AsyncFileExplorerAddon
      v-if="isMounted"
      :is-single-file-mode="isSingleFileMode"
    />
  </ClientOnly>
</template>

<style
  lang="less"
  scoped
>
@import './fileExplorer.style';

</style>

<style lang="less">
@import '@/assets/styles/util.less';
// 这里的样式需要影响 <FileItem />，所以使用 global 作用域
@media screen and (min-width: @mobile-break-width) {
  .file-list {
    &.list {
      .row {
        .operation .pp-link-button {
          display: none;
        }

        &:hover {
          .operation .pp-link-button {
            display: flex;
          }
        }

        &.checked {
          background: rgba(48, 110, 255, 0.1);

          .operation .pp-link-button {
            display: flex;
          }
        }
      }
    }
  }
}
</style>
